/**
 * ImportConfirmDialog Component
 *
 * Confirmation dialog before executing bulk import.
 * Shows summary of what will be imported and warning for skipped records.
 *
 * Features:
 * - Final summary statistics
 * - List of what will be created
 * - Warning for skipped invalid records
 * - Loading state during import
 * - Disabled buttons during import
 *
 * @module ImportExportTab/components/ImportConfirmDialog
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  CircularProgress
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import AdminButton from 'components/common/AdminButton';

/**
 * ImportConfirmDialog Component
 *
 * @param {Object} props
 * @param {boolean} props.open - Dialog open state
 * @param {Object} props.validationResult - Result from csvValidator service
 * @param {string} props.importType - Type of import ("orgUnits" or "locations")
 * @param {Function} props.onCancel - Cancel callback
 * @param {Function} props.onConfirm - Confirm callback
 * @param {boolean} props.loading - Loading state during import
 * @returns {JSX.Element}
 */
function ImportConfirmDialog({
  open,
  validationResult,
  importType,
  onCancel,
  onConfirm,
  loading
}) {
  const { summary } = validationResult;

  const itemTypePlural = importType === 'orgUnits' ? 'OrgUnits' : 'Locations';
  const itemTypeSingular = importType === 'orgUnits' ? 'OrgUnit' : 'Location';

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onCancel}
      maxWidth="sm"
      fullWidth
      aria-labelledby="import-confirm-dialog-title"
    >
      <DialogTitle id="import-confirm-dialog-title">
        Confirm Bulk Import
      </DialogTitle>

      <DialogContent>
        {/* Summary */}
        <Typography variant="body1" gutterBottom>
          You are about to import the following:
        </Typography>

        <List>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon color="success" />
            </ListItemIcon>
            <ListItemText
              primary={`${summary.validCount} ${itemTypePlural} will be imported`}
              secondary={`${summary.validCount} valid ${summary.validCount === 1 ? 'record' : 'records'}`}
            />
          </ListItem>
        </List>

        {/* Warning for skipped records */}
        {summary.invalidCount > 0 && (
          <Alert severity="warning" icon={<WarningIcon />} sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>{summary.invalidCount}</strong> {summary.invalidCount === 1 ? 'record' : 'records'} will be skipped due to validation errors.
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              These records will not be imported. Please fix the errors and re-upload if needed.
            </Typography>
          </Alert>
        )}

        {/* Additional info */}
        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            This action will create {summary.validCount} new {summary.validCount === 1 ? itemTypeSingular : itemTypePlural} in the database.
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            This operation cannot be undone.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onCancel}
          disabled={loading}
          color="inherit"
        >
          Cancel
        </Button>
        <AdminButton
          onClick={onConfirm}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : undefined}
        >
          {loading ? 'Importing...' : `Import ${summary.validCount} ${summary.validCount === 1 ? 'Record' : 'Records'}`}
        </AdminButton>
      </DialogActions>
    </Dialog>
  );
}

ImportConfirmDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  validationResult: PropTypes.shape({
    validRecords: PropTypes.arrayOf(PropTypes.object).isRequired,
    invalidRecords: PropTypes.arrayOf(PropTypes.object).isRequired,
    summary: PropTypes.shape({
      totalRecords: PropTypes.number.isRequired,
      validCount: PropTypes.number.isRequired,
      invalidCount: PropTypes.number.isRequired
    }).isRequired
  }).isRequired,
  importType: PropTypes.oneOf(['orgUnits', 'locations']).isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired
};

export default ImportConfirmDialog;
